import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { Formik, Form, Field } from "formik";
import * as Yup from "yup";
import InputField from "../../../components/inputFields/InputField";
import Button from "../../../components/buttons/Button";
import { cscStates, cscCities } from "../../../constants/csc_data";
import ImagePicker from "../../../components/ImagePicker";
import FormikDropdown from "../../../components/inputFields/FormikDropDown";
import { formateDateFromMinus, formateDateToMinus } from "../../../utils/FormatDateTime";
import { patchPatient, postPatient } from "../../../services/hud/hud_patients";
import { toast } from "react-toastify";
import Breadcrumbs from "../../../components/BreadCrumbs";
import { getHudMachines } from "../../../services/hud/hud_machines";
import FilePickerField from "../../../components/inputFields/FilePickerField";

const PatientForm = () => {
    const { slug } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const [selectedImage, setImage] = useState(null);

    const [initialValues, setInitialValues] = useState({
        full_name: "",
        photo: null,
        patient_type: "New",
        gender: "",
        phone_number: "",
        date_of_birth: "",
        country: "India",
        state: "",
        city: "",
        skin_type: "V",
        system_code: "",
        machine_name: "",
    });

    const [filteredCities, setFilteredCities] = useState([]);
    const isEditMode = slug !== "add";

    const handleImage = (file) => {
        if (file instanceof File || file instanceof Blob) {
            setImage(file);
        } else {
            console.error("Invalid file selected:", file);
        }
    };

    useEffect(() => {
        if (isEditMode) {
            const patientData = location.state;
            if (patientData) {
                setImage(patientData.photo);
                setInitialValues({
                    full_name: patientData.full_name || "",
                    photo: patientData.photo || null,
                    patient_type: patientData.patient_type || "New",
                    gender: patientData.gender || "",
                    phone_number: patientData.phone_number || "",
                    date_of_birth: formateDateFromMinus(patientData.date_of_birth || ""),
                    country: "India",
                    state: patientData.state || "",
                    city: patientData.city || "",
                    skin_type: patientData.skin_type || "V",
                    system_code: patientData.system_code || "",
                    machine_name: patientData.machine_name || "",
                });

                setFilteredCities(
                    patientData.state ? cscCities[0][patientData.state] || [] : []
                );
            }
        }
    }, [isEditMode, location.state]);


    const handleStateChange = (state) => {
        setFilteredCities(state ? cscCities[0][state] || [] : []);
    };

    const validationSchema = Yup.object({
        full_name: Yup.string().required("Full Name is required"),
        patient_type: Yup.string().required("Patient type is required"),
        gender: Yup.string().required("Gender is required"),
        phone_number: Yup.string().required("Phone number is required"),
        date_of_birth: Yup.string().required("Date of Birth is required"),
        state: Yup.string().required("State is required"),
        city: Yup.string().required("City is required"),
        system_code: Yup.string().required("System code is required"),
    });

    const handleSubmit = async (values, { setSubmitting }) => {
        setSubmitting(true);
        const formData = new FormData();
        formData.append("full_name", values.full_name);
        if (selectedImage && typeof selectedImage === "object") {
            formData.append("photo", selectedImage);
        }
        formData.append("patient_type", values.patient_type);
        formData.append("gender", values.gender);
        formData.append("phone_number", values.phone_number);
        formData.append("date_of_birth", formateDateToMinus(values.date_of_birth));
        formData.append("country", values.country);
        formData.append("state", values.state);
        formData.append("city", values.city);
        formData.append("skin_type", values.skin_type);
        formData.append("system_code", values.system_code);
        if (!isEditMode) {
            formData.append("machine_id", values.machine_id);

        }
        try {
            if (isEditMode) {
                // Edit operation
                const response = await patchPatient(location.state.patient_id, formData);
                if (response?.status === 200) {
                    toast.success("Patient updated successfully!");
                    navigate("/hud-patients");
                } else {
                    toast.error("Oops! Something went wrong, please try again.");
                }
            } else {
                // Create operation
                const response = await postPatient(formData);
                if (response?.status === 201) {
                    toast.success("Patient added successfully!");
                    navigate("/hud-patients");
                } else {
                    toast.error("Oops! Something went wrong, please try again.");
                }
            }
        } catch (error) {
            let errors =
                error?.response?.data?.error ||
                "Oops! Something went wrong, please try again.";
            toast.error(errors);
        } finally {
            setSubmitting(false);
        }
    };

    const [machines, setMachines] = useState([]);

    const fetchCenters = async () => {
        try {
            let res = await getHudMachines();
            if (res.status === 200) {
                setMachines(res.data.results);
            } else {
                toast.error("Unable to fetch Machines");
            }
        } catch (error) {
            console.error("Error fetching Machines:", error);
        }
    };


    useEffect(() => {
        if (!isEditMode) {
            fetchCenters();
        }
    }, []);

    return (
        <div className="p-6 bg-white rounded-lg shadow-md">
            <Breadcrumbs items={[
                { name: 'Home', link: '/invoice' },
                { name: 'Patients', link: '/hud-patients' },
                { name: isEditMode ? "Edit Patient" : "Add Patient" },
            ]} />
            <h2 className="text-xs font-semibold my-4">
                {isEditMode ? "Edit Patient" : "Add Patient"}
            </h2>
            <Formik
                initialValues={initialValues}
                validationSchema={validationSchema}
                onSubmit={handleSubmit}
                enableReinitialize
            >
                {({ isSubmitting, setFieldValue, values }) => (
                    <Form className="space-y-4">
                        {/* <div key={values.id} className="w-48 h-48">
                            <ImagePicker
                                label="Patient Photo"
                                preloadedImage={selectedImage}
                                onImageSelect={handleImage}
                            />
                        </div> */}
                        <div className="grid gap-6 md:grid-cols-3 lg:grid-cols-4">
                            <InputField
                                label="Full Name"
                                name="full_name"
                                type="text"
                                placeholder="Enter patient's full name"
                                required={!isEditMode}
                                disable={isEditMode}
                            />
                            <FormikDropdown
                                label="Patient Type"
                                name="patient_type"
                                placeholder="Select Patient Type"
                                required={!isEditMode}
                                disabled={isEditMode}
                                options={[
                                    { label: "New", value: "New" },
                                    { label: "Old", value: "Old" },
                                ]}
                            />
                            <InputField
                                label="Date of Birth"
                                name="date_of_birth"
                                type="date"
                                required={!isEditMode}
                                disable={isEditMode}
                            />
                            <FormikDropdown
                                label="Gender"
                                name="gender"
                                placeholder="Select Gender"
                                required={!isEditMode}
                                disabled={isEditMode}
                                options={[
                                    { label: "Male", value: "M" },
                                    { label: "Female", value: "F" },
                                    { label: "Other", value: "O" },
                                ]}
                            />
                            <InputField
                                label="Phone Number"
                                name="phone_number"
                                type="text"
                                placeholder="Enter phone number"
                                required={!isEditMode}
                                disable={isEditMode}
                            />


                            <FormikDropdown
                                label="Country"
                                name="country"
                                placeholder="Select Country"
                                required={!isEditMode}
                                options={[{ label: "India", value: "India" }]}
                                disabled
                            />

                            <FormikDropdown
                                label="State"
                                name="state"
                                placeholder="Select State"
                                required={!isEditMode}
                                needSearch
                                disabled={isEditMode}
                                options={cscStates.map((state) => ({
                                    label: state.name,
                                    value: state.name,
                                }))}
                                onChange={(e) => {
                                    setFieldValue("state", e.value);
                                    setFieldValue("city", "");
                                    handleStateChange(e.value);
                                }}
                            />
                            <FormikDropdown
                                label="City"
                                name="city"
                                placeholder="Select City"
                                required={!isEditMode}
                                needSearch
                                disabled={isEditMode}
                                options={filteredCities.map((city) => ({
                                    label: city.name,
                                    value: city.name,
                                }))}
                            />
                            <InputField
                                label="Skin Type"
                                name="skin_type"
                                type="text"
                                placeholder="Enter skin type"
                                disable={isEditMode}
                            />
                            <FilePickerField
                                label="Patient Photo"
                                preloadedImage={selectedImage}
                                onFileSelect={handleImage}
                                acceptedFormats=".jpg,.png,.jpeg"
                                disabled={isEditMode}
                            />
                            <FormikDropdown
                                label="System Code"
                                name="system_code"
                                placeholder="Select System Code"
                                required={!isEditMode}
                                needSearch
                                disabled={isEditMode}
                                options={[
                                    { label: "TBD", value: "TBD" },
                                    { label: "NB21", value: "NB21" },
                                    { label: "COMBI 18", value: "COMBI 18" },
                                    { label: "H&F", value: "H&F" },
                                    { label: "PHOTO", value: "PHOTO" },
                                    { label: "F&S", value: "F&S" },
                                ]}
                            />

                            <InputField
                                label="Machine Name"
                                name="machine_name"
                                type="text"
                                placeholder="Machine"
                                disable={isEditMode}
                            />
                            {!isEditMode && (
                                <FormikDropdown
                                    label="Machine Name"
                                    name="machine_id"
                                    placeholder="Select Machine"
                                    options={machines.map((center) => ({
                                        value: center.machine_id,
                                        label: center.machine_name,
                                    }))}
                                    required={true}
                                />
                            )}
                        </div>

                        <div className="flex gap-4">
                            {!isEditMode && (
                                <Button
                                    type="submit"
                                    className="bg-primary text-white px-4 py-2 text-xs rounded"
                                    disabled={isSubmitting}
                                >
                                    Add
                                </Button>
                            )}
                            <Button
                                type="button"
                                className="bg-gray-500 text-white px-4 py-2 text-xs rounded"
                                onClick={() => navigate("/hud-patients")}
                            >
                                Back
                            </Button>
                        </div>
                    </Form>
                )}
            </Formik>
        </div >
    );
};

export default PatientForm;
